import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { NAME_MAX_LENGTH, PLAYER_COLORS, TOKENS, TOKEN_NAMES } from "@grundbesitz/shared";
import type { PlayerColorId, RoomMember, TokenId } from "@grundbesitz/shared";
import { TokenIcon } from "../board/TokenIcon";
import { Button } from "../ui/Button";
import { saveName, useStore } from "../../lib/store";
import { cleanName } from "../../screens/homeLogic";
import { takenColors, takenTokens } from "./lobbyLogic";

const focusRing =
  "peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[color:var(--focus)]";

/** The viewer's own seat: name, token and color. Every change goes straight to the server. */
export function SeatEditor({ me, members }: { me: RoomMember; members: RoomMember[] }) {
  const lobbyUpdate = useStore((s) => s.lobbyUpdate);
  const nameId = useId();
  const tokenGroup = useId();
  const colorGroup = useId();
  const [name, setName] = useState(me.name);
  const takenT = takenTokens(members, me.id);
  const takenC = takenColors(members, me.id);

  // Follow the server unless the field is being edited.
  useEffect(() => {
    if (document.activeElement?.id !== nameId) setName(me.name);
  }, [me.name, nameId]);

  const submitName = (e: FormEvent) => {
    e.preventDefault();
    const clean = cleanName(name);
    if (!clean) {
      setName(me.name);
      return;
    }
    setName(clean);
    if (clean !== me.name) {
      saveName(clean);
      void lobbyUpdate({ name: clean });
    }
  };

  const dirty = cleanName(name) !== me.name && cleanName(name) !== "";

  return (
    <section aria-labelledby="seat-title" className="space-y-6">
      <h2 id="seat-title" className="font-display text-xl font-semibold text-ink">
        Dein Platz
      </h2>

      <form onSubmit={submitName} className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-56">
          <label htmlFor={nameId} className="mb-1.5 block text-sm font-semibold text-ink">
            Name
          </label>
          <input
            id={nameId}
            value={name}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="nickname"
            onChange={(e) => setName(e.target.value)}
            className="h-11 w-full rounded-lg border border-line bg-surface px-3 text-ink"
          />
        </div>
        <Button type="submit" disabled={!dirty}>
          Name ändern
        </Button>
      </form>

      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-ink">Figur</legend>
        <div className="grid grid-cols-3 gap-2 min-[420px]:grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 xl:grid-cols-6">
          {TOKENS.map((token: TokenId) => {
            const owner = takenT.get(token);
            const checked = me.token === token;
            return (
              <label key={token} className="relative block">
                <input
                  type="radio"
                  name={tokenGroup}
                  className="peer sr-only"
                  checked={checked}
                  disabled={owner !== undefined}
                  onChange={() => void lobbyUpdate({ token })}
                />
                <span
                  title={owner ? `${TOKEN_NAMES[token]}, vergeben an ${owner.name}` : TOKEN_NAMES[token]}
                  className={`flex min-h-[5.25rem] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-line bg-surface px-1 py-2 text-center text-ink hover:bg-surface-2 peer-checked:border-accent peer-checked:bg-accent-soft peer-disabled:cursor-not-allowed peer-disabled:bg-surface-2 peer-disabled:opacity-60 ${focusRing}`}
                >
                  <TokenIcon token={token} color={me.color ?? undefined} size={34} />
                  <span className="text-xs font-medium leading-tight">{TOKEN_NAMES[token]}</span>
                  {owner && <span className="max-w-full truncate text-xs leading-tight text-ink-2">{owner.name}</span>}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-ink">Farbe</legend>
        <div className="grid grid-cols-4 gap-2">
          {PLAYER_COLORS.map((c) => {
            const owner = takenC.get(c.id as PlayerColorId);
            const checked = me.color === c.id;
            return (
              <label key={c.id} className="relative block">
                <input
                  type="radio"
                  name={colorGroup}
                  className="peer sr-only"
                  checked={checked}
                  disabled={owner !== undefined}
                  onChange={() => void lobbyUpdate({ color: c.id })}
                />
                <span
                  title={owner ? `${c.name}, vergeben an ${owner.name}` : c.name}
                  className={`flex min-h-[4.75rem] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-line bg-surface px-1 py-2 text-center text-ink hover:bg-surface-2 peer-checked:border-accent peer-checked:bg-accent-soft peer-disabled:cursor-not-allowed peer-disabled:bg-surface-2 peer-disabled:opacity-60 ${focusRing}`}
                >
                  <span
                    aria-hidden="true"
                    className="grid size-7 place-items-center rounded-full text-sm font-bold"
                    style={{ background: c.hex, color: c.id === "amber" ? "#1e1c17" : "#ffffff" }}
                  >
                    {checked ? "✓" : owner ? "×" : ""}
                  </span>
                  <span className="text-xs font-medium leading-tight">{c.name}</span>
                  {owner && <span className="max-w-full truncate text-xs leading-tight text-ink-2">{owner.name}</span>}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
}
